import { useState } from "react";
import { readContractFlags, summariseFlags } from "../../shared/security";
import { isoDay } from "../lib/format";

// The contract safety panel. This is the most differentiated thing on a token
// page: an automated read of what the contract actually permits, in language
// someone can act on.
//
// Two rules it exists to honour:
//   1. An unscanned contract is UNKNOWN, never "safe". Absence of evidence is
//      not evidence of safety, and this panel says so out loud.
//   2. A flag is shown with what it MEANS for a holder, not just its name.

const EXPLORERS: Record<string, string> = {
  ethereum: "https://etherscan.io",
  base: "https://basescan.org",
  arbitrum: "https://arbiscan.io",
  optimism: "https://optimistic.etherscan.io",
  polygon: "https://polygonscan.com",
};

function explorerFor(chain: string | null | undefined, address: string): string | null {
  const base = chain ? EXPLORERS[chain.toLowerCase()] : null;
  return base ? `${base}/token/${address}` : null;
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="copy-btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {
          // Clipboard can be blocked; the full address is on screen regardless.
        }
      }}
      aria-label={copied ? "Address copied" : "Copy address"}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export function ContractSafety({
  chain,
  contractAddress,
  deployerAddress,
  securityFlags,
  securityScannedAt,
}: {
  chain?: string | null;
  contractAddress?: string | null;
  deployerAddress?: string | null;
  securityFlags?: Record<string, unknown>;
  securityScannedAt?: string | null;
}) {
  // Nothing to say about a token with no contract — a native coin, for one.
  if (!contractAddress) return null;

  const flags = readContractFlags(securityFlags);
  const summary = summariseFlags(flags);
  const explorer = explorerFor(chain, contractAddress);

  return (
    <section className="section">
      <div className="row space-between wrap gap-8">
        <h2>Contract safety</h2>
        <span className={`safety-pill safety-${summary.worst}`}>{summary.headline}</span>
      </div>

      <p className="muted safety-intro">
        An automated read of what this contract permits. It describes code, not the team's
        intentions, and it cannot detect every risk.
      </p>

      <div className="addr-block">
        <div>
          <span className="addr-label">Contract address</span>
          <code className="addr">{contractAddress}</code>
        </div>
        <div className="row gap-8">
          <CopyButton value={contractAddress} />
          {explorer && (
            <a className="btn btn-ghost btn-sm" href={explorer} target="_blank" rel="noopener noreferrer nofollow">
              View on explorer ↗
            </a>
          )}
        </div>
      </div>
      <p className="muted addr-hint">
        Check this against the address you are about to trade. Impersonating a well-known token
        with a different contract is one of the most common ways people lose money.
      </p>

      {flags ? (
        <>
          <ul className="flag-list">
            {flags.map((f) => (
              <li key={f.key} className={`flag flag-${f.severity}`}>
                <span className="flag-icon" aria-hidden>
                  {f.severity === "good" ? "✓" : f.severity === "critical" ? "!" : f.severity === "caution" ? "!" : "i"}
                </span>
                <span>
                  <b>{f.heading}</b>
                  <small>{f.meaning}</small>
                </span>
              </li>
            ))}
          </ul>
          {securityScannedAt && (
            <p className="muted scan-date">
              Last scanned {isoDay(securityScannedAt)}. Contracts can be
              upgraded, so a scan describes the code as it was on that date.
            </p>
          )}
        </>
      ) : (
        <div className="notice notice-error unscanned">
          <b>Not scanned yet.</b> That is not the same as safe — we simply have no result for this
          contract. Verify it yourself on the explorer before trusting it.
        </div>
      )}

      {deployerAddress && (
        <p className="muted deployer">
          Deployed by <code>{deployerAddress}</code>
        </p>
      )}
    </section>
  );
}
